import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  competitionsApi,
  type CompetitionListParams,
  type CreateCompetitionPayload,
  type UpdateCompetitionPayload,
} from '@/api/competitions';
import { usersApi } from '@/api/users';
import type { UpdateCompetitionLandingPayload } from '@/api/types';

export const competitionKeys = {
  all: ['competitions'] as const,
  lists: () => [...competitionKeys.all, 'list'] as const,
  list: (params: CompetitionListParams) => [...competitionKeys.lists(), params] as const,
  details: () => [...competitionKeys.all, 'detail'] as const,
  detail: (id: string) => [...competitionKeys.details(), id] as const,
};

export function useCompetitions(params: CompetitionListParams = {}) {
  return useQuery({
    queryKey: competitionKeys.list(params),
    queryFn: () => competitionsApi.list(params),
  });
}

export function useCompetition(id: string | undefined) {
  return useQuery({
    queryKey: competitionKeys.detail(id ?? ''),
    queryFn: () => competitionsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCompetitionLanding(id: string | undefined) {
  return useQuery({
    queryKey: [...competitionKeys.detail(id ?? ''), 'landing'] as const,
    queryFn: () => competitionsApi.getLanding(id as string),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useUploadCompetitionAnnouncement(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => competitionsApi.uploadAnnouncement(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) }),
  });
}

export function useRemoveCompetitionAnnouncement(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => competitionsApi.removeAnnouncement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) }),
  });
}

export function useUpdateCompetitionLanding(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCompetitionLandingPayload) => competitionsApi.updateLanding(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

/** The current user's relationship (participant/judge/manager) to one specific competition. */
export function useCompetitionContext(id: string | undefined) {
  return useQuery({
    queryKey: [...competitionKeys.detail(id ?? ''), 'my-context'] as const,
    queryFn: () => competitionsApi.getMyContext(id as string),
    enabled: Boolean(id),
  });
}

/** Every competition the calling user manages, judges, or participates in - powers the competition switcher and "browse to join" list. */
export function useMyCompetitions() {
  return useQuery({
    queryKey: [...competitionKeys.all, 'mine'] as const,
    queryFn: () => usersApi.myCompetitions(),
  });
}

export function useSetOpenForSignup(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isOpenForSignup: boolean) => competitionsApi.setOpenForSignup(id, isOpenForSignup),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

export function useSetFzsPatronage(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isFzsPatronage: boolean) => competitionsApi.setFzsPatronage(id, isFzsPatronage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

/** Self-service: the calling user joins a competition open for signup (instantly a competitor). */
export function useRequestToJoinCompetition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (competitionId: string) => competitionsApi.requestToJoin(competitionId),
    onSuccess: (_data, competitionId) => {
      queryClient.invalidateQueries({ queryKey: [...competitionKeys.all, 'mine'] });
      queryClient.invalidateQueries({ queryKey: [...competitionKeys.detail(competitionId), 'my-context'] });
    },
  });
}

export function useCreateCompetition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCompetitionPayload) => competitionsApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionKeys.lists() }),
  });
}

export function useUpdateCompetition(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCompetitionPayload) => competitionsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

export function useUploadCompetitionLogo(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => competitionsApi.uploadLogo(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

export function useDeleteCompetition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) => competitionsApi.remove(id, force),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: competitionKeys.lists() }),
  });
}

/** Shared implementation for the lifecycle-transition mutations below. */
function useLifecycleMutation(action: (id: string) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => action(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(id) });
    },
  });
}

export function useScheduleCompetition() {
  return useLifecycleMutation(competitionsApi.schedule);
}

export function useActivateCompetition() {
  return useLifecycleMutation(competitionsApi.activate);
}

export function useStartReviewCompetition() {
  return useLifecycleMutation(competitionsApi.startReview);
}

export function useRevertToReviewCompetition() {
  return useLifecycleMutation(competitionsApi.revertToReview);
}

export function useStartJudgingCompetition() {
  return useLifecycleMutation(competitionsApi.startJudging);
}

export function useStartAwardingCompetition() {
  return useLifecycleMutation(competitionsApi.startAwarding);
}

export function useRevertToJudgingCompetition() {
  return useLifecycleMutation(competitionsApi.revertToJudging);
}

export function useReopenAwardingCompetition() {
  return useLifecycleMutation(competitionsApi.reopenAwarding);
}

/** Applies the exhibition selection; refreshes the competition and the ranking (which carries each entry's flag). */
export function useSetExhibitionSelection(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (percent: number) => competitionsApi.setExhibitionSelection(competitionId, percent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competitionKeys.detail(competitionId) });
      queryClient.invalidateQueries({ queryKey: ['judging', 'ranking', competitionId] });
    },
  });
}

export function useCompleteCompetition() {
  return useLifecycleMutation(competitionsApi.complete);
}

export function useArchiveCompetition() {
  return useLifecycleMutation(competitionsApi.archive);
}

export function useRevertToActiveCompetition() {
  return useLifecycleMutation(competitionsApi.revertToActive);
}
